import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { usePosts } from '@/hooks/usePosts';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Skin tone system — medium-dark (brown) is the BLVX default
export const SKIN_TONES = [
  { key: 'medium-dark',  modifier: '\u{1F3FE}' }, // 🏾 default — brown
  { key: 'dark',         modifier: '\u{1F3FF}' }, // 🏿
  { key: 'medium',       modifier: '\u{1F3FD}' }, // 🏽
  { key: 'medium-light', modifier: '\u{1F3FC}' }, // 🏼
  { key: 'light',        modifier: '\u{1F3FB}' }, // 🏻
  { key: 'none',         modifier: ''           }, // default yellow
];

export const DEFAULT_SKIN_TONE = '\u{1F3FE}'; // medium-dark, brown

export const getSkinTone = () =>
  localStorage.getItem('blvx_skin_tone') ?? DEFAULT_SKIN_TONE;

export const setSkinTone = (modifier) =>
  localStorage.setItem('blvx_skin_tone', modifier);

export const REACTIONS = [
  { key: 'fire',      baseEmoji: '🔥', skinToned: false, label: 'Fire'      },
  { key: 'real_talk', baseEmoji: '✊', skinToned: true,  label: 'Real Talk'  },
  { key: 'crown',     baseEmoji: '👑', skinToned: false, label: 'Crown'      },
  { key: 'crying',    baseEmoji: '😭', skinToned: false, label: 'Crying'     },
  { key: 'side_eye',  baseEmoji: '👀', skinToned: false, label: 'Side Eye'   },
  { key: 'hundred',   baseEmoji: '💯', skinToned: false, label: 'Hundred'    },
  { key: 'clap',      baseEmoji: '👏', skinToned: true,  label: 'Clap'       },
  { key: 'raised',    baseEmoji: '🙌', skinToned: true,  label: 'Raised'     },
  { key: 'skull',     baseEmoji: '💀', skinToned: false, label: 'Dead'       },
  { key: 'prayer',    baseEmoji: '🙏', skinToned: true,  label: 'Prayer'     },
  { key: 'heart',     baseEmoji: '🖤', skinToned: false, label: 'Love'       },
  { key: 'cap',       baseEmoji: '🧢', skinToned: false, label: 'Cap'        },
];

export const getReactionEmoji = (reaction, skinTone = DEFAULT_SKIN_TONE) => {
  if (!reaction.skinToned) return reaction.baseEmoji;
  return reaction.baseEmoji + skinTone;
};

/**
 * Shared post action logic used by both PostCard and FeedCard.
 * Handles plate/like, poll voting, delete, share.
 */
export const usePostActions = (post) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { likePost, unlikePost, deletePost, checkLiked, votePoll, reactToPost, removeReaction } = usePosts();

  const isValidPost = post && post.post_id;
  const feedProvidedLiked = isValidPost && post.user_liked !== undefined && post.user_liked !== null;

  const [isPlated, setIsPlated] = useState(() =>
    isValidPost ? Boolean(post.user_liked) : false
  );
  const [plateCount, setPlateCount] = useState(isValidPost ? (post.like_count || 0) : 0);
  const [checkingLike, setCheckingLike] = useState(!feedProvidedLiked);
  const [pollData, setPollData] = useState(isValidPost ? post.poll : null);
  const [votedIndex, setVotedIndex] = useState(null);
  const [voting, setVoting] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [accountabilityOpen, setAccountabilityOpen] = useState(false);
  const [isPraised, setIsPraised] = useState(isValidPost ? (post.user_praised || false) : false);
  const [praiseCount, setPraiseCount] = useState(isValidPost ? (post.praise_count || 0) : 0);
  const [userReaction, setUserReaction] = useState(isValidPost ? (post.user_reaction || null) : null);
  const [reactionCounts, setReactionCounts] = useState(isValidPost ? (post.reaction_counts || {}) : {});

  const isOwner = user?.user_id === post?.user_id;

  // Plate state — prefer user_liked from feed to avoid N+1 /liked calls
  useEffect(() => {
    if (!isValidPost) return;
    if (feedProvidedLiked) {
      setIsPlated(Boolean(post.user_liked));
      setCheckingLike(false);
      return;
    }
    const check = async () => {
      try {
        const plated = await checkLiked(post.post_id);
        setIsPlated(plated);
      } catch {
        // Ignore
      } finally {
        setCheckingLike(false);
      }
    };
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post?.post_id, post?.user_liked, feedProvidedLiked]);

  // Check if user already voted on this poll
  useEffect(() => {
    if (!isValidPost || !post.poll) return;
    if (user && post.poll.voters?.includes(user.user_id)) {
      setVotedIndex(-1);
    }
  }, [isValidPost, post?.poll, user]);

  const handlePlate = useCallback(async (e) => {
    if (e) e.stopPropagation();
    if (!isValidPost || checkingLike) return;
    try {
      if (isPlated) {
        await unlikePost(post.post_id);
        setPlateCount(prev => prev - 1);
        setIsPlated(false);
      } else {
        await likePost(post.post_id);
        setPlateCount(prev => prev + 1);
        setIsPlated(true);
        toast.success('Plate served!');
      }
    } catch {
      toast.error('Action failed');
    }
  }, [isValidPost, checkingLike, isPlated, post?.post_id, unlikePost, likePost]);

  const handleDelete = useCallback(async () => {
    try {
      await deletePost(post.post_id);
      toast.success('Post deleted');
    } catch {
      toast.error('Failed to delete');
    }
  }, [post?.post_id, deletePost]);

  const handleShare = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/post/${post.post_id}`);
      toast.success('Link copied!');
    } catch {
      toast.error('Failed to copy');
    }
  }, [post?.post_id]);

  const handleVote = useCallback(async (optionIndex) => {
    if (!isValidPost || voting || votedIndex !== null) return;
    setVoting(true);
    try {
      const result = await votePoll(post.post_id, optionIndex);
      setPollData(result.poll);
      setVotedIndex(result.voted);
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Vote failed';
      toast.error(msg);
    } finally {
      setVoting(false);
    }
  }, [isValidPost, voting, votedIndex, post?.post_id, votePoll]);

  const handleReact = useCallback(async (reactionKey) => {
    if (!isValidPost) return;
    try {
      let result;
      if (userReaction === reactionKey) {
        // Toggle off
        result = await removeReaction(post.post_id);
        setUserReaction(null);
      } else {
        result = await reactToPost(post.post_id, reactionKey);
        setUserReaction(reactionKey);
      }
      setReactionCounts(result.reaction_counts || {});
    } catch {
      toast.error('Reaction failed');
    }
  }, [isValidPost, userReaction, post?.post_id, reactToPost, removeReaction]);

  const handlePraise = useCallback(async (e) => {
    if (e) e.stopPropagation();
    if (!isValidPost) return;
    try {
      if (isPraised) {
        const res = await axios.delete(`${API}/posts/${post.post_id}/praise`, { withCredentials: true });
        setPraiseCount(res.data.praise_count);
        setIsPraised(false);
      } else {
        const res = await axios.post(`${API}/posts/${post.post_id}/praise`, {}, { withCredentials: true });
        setPraiseCount(res.data.praise_count);
        setIsPraised(true);
        toast("Let 'em cook!", { icon: '\u{1F373}' });
      }
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Action failed';
      toast.error(msg);
    }
  }, [isValidPost, isPraised, post?.post_id]);

  const goToPost = useCallback(() => {
    if (isValidPost) navigate(`/post/${post.post_id}`);
  }, [isValidPost, post?.post_id, navigate]);

  return {
    isValidPost,
    isOwner,
    isPlated,
    plateCount,
    checkingLike,
    pollData,
    votedIndex,
    voting,
    replyOpen,
    setReplyOpen,
    quoteOpen,
    setQuoteOpen,
    reportOpen,
    setReportOpen,
    accountabilityOpen,
    setAccountabilityOpen,
    isPraised,
    praiseCount,
    handlePraise,
    userReaction,
    reactionCounts,
    handlePlate,
    handleDelete,
    handleShare,
    handleVote,
    handleReact,
    goToPost,
  };
};
